import {
    TextField,
    FormControlLabel,
    Checkbox,
    Box,
    Button,
    Paper,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { getUserByIdThunk, updateUserThunk, getAllUsersThunk } from '@redux/features/user-listing.slice';
import { getTiersThunk } from '@redux/features/tier.slice';
import { Tier } from '@core/models/tier.model';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import FormModal from 'Common/Components/Modal/FormModal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import GlnIdentityFields from 'components/integrations/GlnIdentityFields';
import { useFormik } from 'formik';
import React, { FC, useEffect, useMemo } from 'react';
import * as Yup from 'yup';

interface IProps {
    show: boolean;
    handleClose: () => void;
    userId: string;
}

const UpdateUserModal: FC<IProps> = ({ show, handleClose, userId }) => {
    const { loading, user } = useAppSelector(
        (state: RootState) => (state as any).userListingState || { loading: false, user: null }
    );
    const { tiers } = useAppSelector((state: RootState) => (state as any).tierState || { tiers: [] });
    const dispatch = useAppDispatch();
    // const validationSchema = Yup.object({
    //     businessLicenceNumber: Yup.string(),
    //     statePharmacyLicenseNumber: Yup.string().required('State Pharmacy License Number is required'),
    //     businessLicenceExpirationDate: Yup.string().required('Business Licence Expiration Date is required'),
    //     deaNumber: Yup.string().required('DEA Number is required'),
    //     tierType: Yup.string().required('Tier Type is required'),
    // });

    const handleSubmit = (values: any) => {
        const isPharmacy = user?.account?.accountType === 'PHARMACY';
        const body: any = {
            id: user?.id || '',
            email: user?.email || '',
            accountType: user?.account?.accountType || '',
            businessLicenceNumber: values.businessLicenceNumber,
            statePharmacyLicenseNumber: values.statePharmacyLicenseNumber,
            businessLicenceExpirationDate: values.businessLicenceExpirationDate,
            resaleCertificate: values.resaleCertificate,
            businessLicencePath: values.businessLicencePath,
            isTerm30: values.isTerm30,
            isTerm60: values.isTerm60,
            tierId: values.tierId,
            deaNumber: values.deaNumber,
        };

        // Only include PHARMACY-specific fields if account type is PHARMACY
        if (isPharmacy) {
            body.deaCertificate = values.deaCertificate;
            body.statePharmacyLicence = values.statePharmacyLicence;
            if (values.pharmacyGln) {
                body.pharmacyGln = values.pharmacyGln;
            }
            if (values.pharmacyCompanyPrefix?.trim()) {
                body.pharmacyCompanyPrefix = values.pharmacyCompanyPrefix.trim();
            }
            if (values.pharmacySoldToGln?.trim()) {
                body.pharmacySoldToGln = values.pharmacySoldToGln.trim();
            }
            if (values.pharmacyShipToGln?.trim()) {
                body.pharmacyShipToGln = values.pharmacyShipToGln.trim();
            }
            if (values.shippingAddressLine1?.trim()) {
                body.shippingAddressLine1 = values.shippingAddressLine1.trim();
            }
            if (values.shippingCity?.trim()) {
                body.shippingCity = values.shippingCity.trim();
            }
            if (values.shippingState?.trim()) {
                body.shippingState = values.shippingState.trim();
            }
            if (values.shippingZipCode?.trim()) {
                body.shippingZipCode = values.shippingZipCode.trim();
            }
        }
        dispatch(updateUserThunk({ data: body })).then((res: any) => {
            if (res.meta.requestStatus === 'fulfilled') {
                dispatch(getAllUsersThunk({ pageNumber: '0', pageSize: '10' }));
                handleClose();
                formik.resetForm();
            }
        });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            businessLicenceNumber: user?.account?.businessLicenceNumber ?? '',
            statePharmacyLicenseNumber: user?.account?.statePharmacyLicenseNumber ?? '',
            businessLicenceExpirationDate: user?.account?.businessLicenceExpirationDate ?? '',
            deaCertificate: user?.account?.deaCertificatePath ?? '',
            resaleCertificate: user?.account?.resaleCertificatePath ?? '',
            businessLicencePath: user?.account?.businessLicencePath ?? '',
            statePharmacyLicence: user?.account?.statePharmacyLicencePath ?? '',
            isTerm30: user?.account?.terms?.term30 ?? false,
            isTerm60: user?.account?.terms?.term60 ?? false,
            tierId: user?.account?.tier.tierId ?? '',
            deaNumber: user?.account?.deaNumber ?? '',
            pharmacyGln: user?.account?.pharmacyGln ?? '',
            pharmacyCompanyPrefix: user?.account?.pharmacyCompanyPrefix ?? '',
            pharmacySoldToGln: user?.account?.pharmacySoldToGln ?? '',
            pharmacyShipToGln: user?.account?.pharmacyShipToGln ?? '',
            shippingAddressLine1: user?.account?.shippingAddress?.addressLine1 ?? '',
            shippingCity: user?.account?.shippingAddress?.city ?? '',
            shippingState: user?.account?.shippingAddress?.state ?? '',
            shippingZipCode: user?.account?.shippingAddress?.zipCode ?? '',
        },
        // validationSchema,
        onSubmit: handleSubmit,
    });

    const previews = useMemo(() => {
        const toUrl = (f?: File) => (f && f?.type?.startsWith('image/') ? URL.createObjectURL(f) : null);
        return {
            deaCertificate: toUrl(formik.values.deaCertificate),
            resaleCertificate: toUrl(formik.values.resaleCertificate),
            businessLicencePath: toUrl(formik.values.businessLicencePath),
            statePharmacyLicence: toUrl(formik.values.statePharmacyLicence),
        } as Record<string, string | null>;
    }, [
        formik.values.deaCertificate,
        formik.values.resaleCertificate,
        formik.values.businessLicencePath,
        formik.values.statePharmacyLicence,
    ]);

    useEffect(() => {
        if (show && userId) {
            dispatch(getUserByIdThunk({ userId }));
            dispatch(getTiersThunk());
        }
    }, [show, userId, dispatch]);

    useEffect(() => {
        if (user && show) {
            formik.setValues({
                businessLicenceNumber: user?.account?.businessLicenceNumber ?? '',
                statePharmacyLicenseNumber: user?.account?.statePharmacyLicenseNumber ?? '',
                businessLicenceExpirationDate: user?.account?.businessLicenceExpirationDate ?? '',
                deaCertificate: undefined as unknown as File,
                resaleCertificate: undefined as unknown as File,
                businessLicencePath: undefined as unknown as File,
                statePharmacyLicence: undefined as unknown as File,
                isTerm30: user?.account?.terms?.term30 ?? false,
                isTerm60: user?.account?.terms?.term60 ?? false,
                tierId: user?.account?.tier.tierId ?? '',
                deaNumber: user?.account?.deaNumber ?? '',
                pharmacyGln: user?.account?.pharmacyGln ?? '',
                pharmacyCompanyPrefix: user?.account?.pharmacyCompanyPrefix ?? '',
                pharmacySoldToGln: user?.account?.pharmacySoldToGln ?? '',
                pharmacyShipToGln: user?.account?.pharmacyShipToGln ?? '',
                shippingAddressLine1: user?.account?.shippingAddress?.addressLine1 ?? '',
                shippingCity: user?.account?.shippingAddress?.city ?? '',
                shippingState: user?.account?.shippingAddress?.state ?? '',
                shippingZipCode: user?.account?.shippingAddress?.zipCode ?? '',
            });
        }
    }, [user, show]);

    return (
        <FormModal
            show={show}
            onHide={handleClose}
            title="Update User"
            onSubmit={formik.handleSubmit}
            dialogClassName="relative z-[1050] mx-auto mt-10 w-full max-w-[1000px]"
            footer={
                <>
                    <ButtonBase
                        innerText="Cancel"
                        className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                        onClick={() => {
                            handleClose();
                            formik.resetForm();
                        }}
                        disabled={loading}
                    />
                    <ButtonBase
                        innerText="Update User"
                        className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={formik.handleSubmit}
                        disabled={loading}
                    />
                </>
            }
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField
                        size="small"
                        fullWidth
                        id="businessLicenceNumber"
                        name="businessLicenceNumber"
                        label="Business Licence Number"
                        value={formik.values.businessLicenceNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.businessLicenceNumber && Boolean(formik.errors.businessLicenceNumber)}
                        helperText={
                            formik.touched.businessLicenceNumber
                                ? String(formik.errors.businessLicenceNumber || '')
                                : ''
                        }
                        disabled={loading}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="statePharmacyLicenseNumber"
                        name="statePharmacyLicenseNumber"
                        label="State Pharmacy License Number"
                        value={formik.values.statePharmacyLicenseNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.statePharmacyLicenseNumber &&
                            Boolean(formik.errors.statePharmacyLicenseNumber)
                        }
                        helperText={
                            formik.touched.statePharmacyLicenseNumber
                                ? String(formik.errors.statePharmacyLicenseNumber || '')
                                : ''
                        }
                        disabled={loading}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="businessLicenceExpirationDate"
                        name="businessLicenceExpirationDate"
                        label="Business Licence Expiration Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={formik.values.businessLicenceExpirationDate}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.businessLicenceExpirationDate &&
                            Boolean(formik.errors.businessLicenceExpirationDate)
                        }
                        helperText={
                            formik.touched.businessLicenceExpirationDate
                                ? String(formik.errors.businessLicenceExpirationDate || '')
                                : ''
                        }
                        disabled={loading}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        id="deaNumber"
                        name="deaNumber"
                        label="DEA Number"
                        value={formik.values.deaNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.deaNumber && Boolean(formik.errors.deaNumber)}
                        helperText={formik.touched.deaNumber ? String(formik.errors.deaNumber || '') : ''}
                        disabled={loading}
                    />
                    {user?.account?.accountType === 'PHARMACY' && (
                        <Box className="col-span-full">
                            <GlnIdentityFields
                                mode="pharmacy"
                                disabled={loading}
                                values={{
                                    gln: formik.values.pharmacyGln,
                                    companyPrefix: formik.values.pharmacyCompanyPrefix,
                                    soldToGln: formik.values.pharmacySoldToGln,
                                    shipToGln: formik.values.pharmacyShipToGln,
                                }}
                                onChange={(field, value) => {
                                    const map: Record<string, string> = {
                                        gln: 'pharmacyGln',
                                        companyPrefix: 'pharmacyCompanyPrefix',
                                        soldToGln: 'pharmacySoldToGln',
                                        shipToGln: 'pharmacyShipToGln',
                                    };
                                    formik.setFieldValue(map[field], value);
                                }}
                            />
                            <Typography variant="subtitle2" className="mb-2 mt-2 font-semibold text-slate-700">
                                Ship-to address (EPCIS Location master data)
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                className="col-span-full"
                                id="shippingAddressLine1"
                                name="shippingAddressLine1"
                                label="Street address"
                                value={formik.values.shippingAddressLine1}
                                onChange={formik.handleChange}
                                disabled={loading}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                id="shippingCity"
                                name="shippingCity"
                                label="City"
                                value={formik.values.shippingCity}
                                onChange={formik.handleChange}
                                disabled={loading}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                id="shippingState"
                                name="shippingState"
                                label="State"
                                value={formik.values.shippingState}
                                onChange={formik.handleChange}
                                disabled={loading}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                id="shippingZipCode"
                                name="shippingZipCode"
                                label="ZIP"
                                value={formik.values.shippingZipCode}
                                onChange={formik.handleChange}
                                disabled={loading}
                            />
                        </Box>
                    )}
                    <FormControl fullWidth size="small" disabled={loading}>
                        <InputLabel id="tierType-label">Tier Type</InputLabel>
                        <Select
                            labelId="tierType-label"
                            id="tierType"
                            name="tierId"
                            value={formik.values.tierId}
                            label="Tier Type"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.tierId && Boolean(formik.errors.tierId)}
                        >
                            {tiers?.map((tier: Tier) => (
                                <MenuItem key={tier.id} value={tier.tierId}>
                                    {tier.tierType}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.tierId && formik.errors.tierId && (
                            <Typography variant="caption" color="error" className="mt-1 ml-2">
                                {String(formik.errors.tierId)}
                            </Typography>
                        )}
                    </FormControl>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formik.values.isTerm30}
                                onChange={(e) => formik.setFieldValue('isTerm30', e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Term 30"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formik.values.isTerm60}
                                onChange={(e) => formik.setFieldValue('isTerm60', e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Term 60"
                    />

                    <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(() => {
                            const isPharmacy = user?.account?.accountType === 'PHARMACY';
                            const fileFields = [
                                { key: 'resaleCertificate', label: 'Resale Certificate (file)' },
                                { key: 'businessLicencePath', label: 'Business Licence Path (file)' },
                            ];

                            // Add PHARMACY-specific fields if account type is PHARMACY
                            if (isPharmacy) {
                                fileFields.unshift(
                                    { key: 'deaCertificate', label: 'DEA Certificate (file)' },
                                    { key: 'statePharmacyLicence', label: 'State Pharmacy Licence (file)' }
                                );
                            }

                            return fileFields;
                        })().map((f) => {
                            const file = (formik.values as any)[f.key] as File | undefined;
                            const preview = (previews as any)[f.key] as string | null;
                            return (
                                <Paper key={f.key} variant="outlined" className="p-3 flex items-center gap-3">
                                    <Box className="flex-1">
                                        <Typography variant="body2" className="mb-1 text-slate-700">
                                            {f.label}
                                        </Typography>
                                        {file ? (
                                            <Box className="flex items-center gap-3">
                                                {preview ? (
                                                    // eslint-disable-next-line @next/next/no-img-element
                                                    <img
                                                        src={preview}
                                                        alt="preview"
                                                        className="w-12 h-12 rounded object-cover border"
                                                    />
                                                ) : (
                                                    <Box className="w-12 h-12 rounded border flex items-center justify-center text-xs text-slate-500">
                                                        File
                                                    </Box>
                                                )}
                                                <Box className="min-w-0">
                                                    <Typography variant="body2" className="truncate max-w-[220px]">
                                                        {file.name}
                                                    </Typography>
                                                    <Typography variant="caption" className="text-slate-500">
                                                        {(file.size / 1024).toFixed(1)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" className="text-slate-500">
                                                No file selected
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box className="flex items-center gap-2">
                                        <Button variant="outlined" size="small" component="label" disabled={loading}>
                                            Upload
                                            <input
                                                hidden
                                                type="file"
                                                accept="image/*,.pdf,.doc,.docx,.png,.jpg,.jpeg"
                                                onChange={(e) =>
                                                    (formik as any).setFieldValue(f.key, e.currentTarget.files?.[0])
                                                }
                                            />
                                        </Button>
                                        {file && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => (formik as any).setFieldValue(f.key, undefined)}
                                                disabled={loading}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </div>
            </div>
        </FormModal>
    );
};

export default UpdateUserModal;
